/**
 * Settings — runtime config + auth status + sign-out.
 *
 * Sections:
 *   - Account (current user + sign-out button)            visible to ALL
 *   - Client (Tableau config)                             visible to ADMINS only
 *   - API (base URL, auth method, version)                visible to ADMINS only
 *
 * Non-admin users see only the Account section. The admin-gated sections
 * are hidden from the DOM entirely (not just disabled) so non-admins don't
 * see that configuration UI exists at all.
 */
import { useEffect, useState } from "react";
import { fetchConfig } from "../lib/api";
import type { ServerConfig } from "../lib/api";
import { useAuth } from "../auth/AuthContext";

export function Settings() {
  const { user, logout } = useAuth();
  const [config, setConfig] = useState<ServerConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signingOut, setSigningOut] = useState(false);

  const isAdmin = !!user?.is_admin;

  useEffect(() => {
    // Only admins see config sections — only they need the fetch.
    if (!isAdmin) return;
    fetchConfig()
      .then(setConfig)
      .catch((e) => setError(e.message));
  }, [isAdmin]);

  const apiBase = import.meta.env.VITE_API_BASE_URL || "(proxied via Vite)";

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await logout();
      // logout() navigates away; we won't return here.
    } catch {
      setSigningOut(false);
    }
  }

  return (
    // The app shell's <main> is h-screen overflow-hidden, so each route
    // provides its own scroll container (same pattern as Dashboards).
    <div className="p-8 view-in overflow-y-auto h-full">
      <header className="mb-8">
        <h1
          className="font-serif text-[28px] leading-tight"
          style={{ color: "var(--ink)" }}
        >
          Settings
        </h1>
        <p
          className="text-[14.5px] font-serif italic mt-1"
          style={{ color: "var(--ink-3)" }}
        >
          {isAdmin
            ? "Configuration and connection status."
            : "Your account."}
        </p>
      </header>

      {/* Account — visible to all */}
      <section
        className="rounded-lg border hairline p-6 mb-4"
        style={{ background: "var(--bg-elev)" }}
      >
        <h2
          className="text-[11px] font-mono uppercase tracking-[0.14em] mb-4"
          style={{ color: "var(--brass)" }}
        >
          Account
        </h2>
        {user ? (
          <>
            <dl className="grid grid-cols-[180px_1fr] gap-y-3 gap-x-6 mb-5">
              <Row label="Signed in as" value={user.email} mono />
              <Row
                label="Role"
                value={
                  user.is_admin
                    ? "Administrator"
                    : user.has_ai_analyst
                    ? "Analyst"
                    : "Viewer"
                }
              />
              <Row label="AI Analyst" value={user.has_ai_analyst ? "Enabled" : "Not enabled"} />
              <Row label="Groups" value={user.groups.join(", ") || "(none)"} mono />
            </dl>
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="rounded-md py-2 px-4 font-serif text-[14px] transition-opacity disabled:opacity-50"
              style={{
                background: "var(--ink)",
                color: "var(--bg)",
              }}
            >
              {signingOut ? "Signing out…" : "Sign out"}
            </button>
          </>
        ) : (
          <div
            className="font-serif text-[14px] italic"
            style={{ color: "var(--ink-3)" }}
          >
            Not signed in.
          </div>
        )}
      </section>

      {/* Client — admins only */}
      {isAdmin && (
        <section
          className="rounded-lg border hairline p-6 mb-4"
          style={{ background: "var(--bg-elev)" }}
        >
          <h2
            className="text-[11px] font-mono uppercase tracking-[0.14em] mb-4"
            style={{ color: "var(--brass)" }}
          >
            Client
          </h2>
          {error ? (
            <div
              className="font-mono text-[13px]"
              style={{ color: "var(--oxblood)" }}
            >
              {error}
            </div>
          ) : !config ? (
            <div
              className="font-serif text-[14px] italic"
              style={{ color: "var(--ink-3)" }}
            >
              Loading…
            </div>
          ) : (
            <dl className="grid grid-cols-[180px_1fr] gap-y-3 gap-x-6">
              <Row label="Client name" value={config.client_name} />
              <Row label="Client ID" value={config.client_id} mono />
              <Row label="Tableau server" value={config.tableau_server} mono />
              <Row label="Tableau site" value={config.tableau_site} mono />
              <Row
                label="Dashboards available"
                value={String(config.dashboard_names.length)}
              />
            </dl>
          )}
        </section>
      )}

      {/* API — admins only */}
      {isAdmin && (
        <section
          className="rounded-lg border hairline p-6"
          style={{ background: "var(--bg-elev)" }}
        >
          <h2
            className="text-[11px] font-mono uppercase tracking-[0.14em] mb-4"
            style={{ color: "var(--brass)" }}
          >
            API
          </h2>
          <dl className="grid grid-cols-[180px_1fr] gap-y-3 gap-x-6">
            <Row label="API base URL" value={apiBase || "(empty)"} mono />
            <Row label="Auth method" value="Cognito (HttpOnly cookie)" />
            <Row label="Frontend version" value="0.1.0" mono />
          </dl>
        </section>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt
        className="text-[10.5px] font-mono uppercase tracking-[0.14em] pt-1"
        style={{ color: "var(--ink-3)" }}
      >
        {label}
      </dt>
      <dd
        className={mono ? "font-mono text-[13px]" : "font-serif text-[14.5px]"}
        style={{ color: "var(--ink)" }}
      >
        {value}
      </dd>
    </>
  );
}
